/* ============================================================================
   ENTITIES — mọi thứ TỰ DI CHUYỂN: vật nuôi, sâu bọ, (sau này) người làm, xe.

   Hai luật quyết định toàn bộ file này, và cả hai đều là để giữ TÍNH TẤT ĐỊNH —
   thứ mà save, replay và toàn bộ bộ test sim đang dựa vào.

   LUẬT 1 — `state.seed` là BẤT KHẢ XÂM PHẠM trong đường TICK.
   Trước khi có thực thể, TICK không rút một hạt ngẫu nhiên nào; seed chỉ bị rút
   theo SỰ KIỆN (sang ngày, thu hoạch, mua bán). Phải giữ nguyên như thế. Nếu 20
   con vật cùng rút seed toàn cục mỗi khung hình thì SỐ LẦN rút phụ thuộc số
   khung hình — mà số khung hình phụ thuộc fps, phụ thuộc cả việc có mở modal
   hay không. Bất biến "cùng seed + cùng chuỗi action = state y hệt" vỡ ngay, mà
   vỡ âm thầm.
   → Mỗi con mang `seed` riêng, advance cục bộ. Hạt ban đầu rút từ `state.seed`
     đúng một lần lúc SINH RA, và việc sinh ra luôn là một action — tức ngẫu
     nhiên theo sự kiện, đúng khuôn cũ.
   Kịch bản sim 55 canh cửa đúng chỗ này: chạy vài nghìn TICK rồi so `state.seed`.

   LUẬT 2 — DI CHUYỂN mỗi khung hình, QUYẾT ĐỊNH theo nhịp giờ game.
   Nhích theo đường đi thì làm mỗi khung hình (mượt, thuần, không rút số nào).
   Còn *chọn làm gì* thì chỉ chạy mỗi `ACTOR_STEP_MINUTES` phút game. Số bước là
   hàm của `state.minutes`, mà `minutes` là hàm của tổng `dt` đã dispatch — nên
   replay cùng chuỗi action cho cùng số bước, bất kể máy chạy 30 hay 120 fps.
   Đếm bằng CHỈ SỐ NGUYÊN (`actStep`) chứ không phải bộ tích luỹ float, để không
   trôi qua save/load.
============================================================================ */

import type { AnimalDef, Content, Entity, GameState } from "./types.ts";
import type { Draft } from "./state.ts";
import { activeView, dEntities, dEntity, nextRandom, setEntities, touch } from "./state.ts";
import { blockedForActor, idx, TILE, tileAt,
  dirFromVector,
} from "./world.ts";
import { findPath } from "./pathfind.ts";
import { workerStep } from "./workerai.ts";
import { vehicleStep } from "./vehicles.ts";
import { patrolCatch, isHungry } from "./animals.ts";
import { grazeHere, nearestGraze } from "./graze.ts";
import { eatFromTrough, penGoal, penOf, penWander } from "./pen.ts";
import { weatherMood } from "./weather.ts";
import { shelterSpot } from "./graze.ts";

/** Trần số thực thể. Đủ cho một nông trại đông đúc, đủ thấp để 64 phép so mỗi
 *  khung hình vẫn rẻ hơn một lần `blockedAt`. */
export const MAX_ENTITIES = 64;

/** Một "bước quyết định" = nửa phút game (≈0,25 giây thật ở nhịp mặc định). */
export const ACTOR_STEP_MINUTES = 0.5;

/** Trần số bước bù trong MỘT lần TICK — chặn treo sau `replace`/migrate khi
 *  `minutes` nhảy vọt. */
export const MAX_STEPS_PER_TICK = 8;

/** Mỗi bước chỉ ngần này con được tính đường mới, xoay vòng theo `planCursor`.
 *  Nhờ vậy chi phí A* là HẰNG SỐ, không phụ thuộc số con vật. */
export const MAX_REPLANS_PER_STEP = 2;

/**
 * Trần nút mở của A* cho thực thể.
 *
 * Phải PHỦ NỔI chính vùng nó được phép quét: vùng dây buộc 41×41 là 1681 ô, mà
 * trần cũ là 900 — nên một đường vòng dài (quanh ao, quanh dãy chuồng) cạn ngân
 * sách giữa chừng, trả `null`, và con vật đứng ngơ ở đầu kia hàng rào. Chi phí
 * vẫn là hằng số theo số con: `MAX_REPLANS_PER_STEP` mới là thứ chặn tổng.
 */
export const MAX_NODES_ACTOR = 2000;

/** Đường đi dài nhất giữ trong save. Cắt thì tới cuối tự tính lại. */
export const MAX_PATH = 64;

/**
 * Riêng XE thì dài hơn: chúng chạy men theo đường nhựa từ cổng bản đồ tới tận
 * bãi đậu trước kho — tuyến (30,36) → (42,5) dễ vượt 64 điểm. Cắt cụt thì xe
 * dừng giữa đường rồi tự lập lại, ăn thêm ngân sách A* của cả đàn vật nuôi.
 */
export const MAX_PATH_VEHICLE = 160;

/** Chỉ tìm đường trong bán kính này quanh mốc — một con kẹt góc không được
 *  phép quét cả bản đồ. */
export const LEASH_TILES = 20;

/** Nguội giữa hai lần tính đường của CÙNG một con, tính bằng phút game. */
export const REPLAN_COOLDOWN = 2;

/**
 * Con này còn trong thời gian nguội không — tức là VỪA tính đường xong.
 *
 * Vì sao không viết thẳng `minutes - planAt < REPLAN_COOLDOWN`: `planAt` là một
 * mốc trên ĐỒNG HỒ TRONG NGÀY, mà đồng hồ ấy LÙI mỗi sáng (1560 → 360). Con nào
 * nghĩ lúc 1535 hôm qua thì sáng nay `360 - 1535 = -1175`, nhỏ hơn 2, nên phép
 * so cũ nói "vừa nghĩ xong" và bắt nó nghỉ — suốt tới khi hôm nay trôi qua phút
 * 1537, tức gần trọn một ngày. Đo được đúng cái ấy: bò đứng cạnh máng đầy, dấu
 * chấm than trên đầu, không ăn không đi, từ sáng tới tối.
 *
 * `newDay` đã đặt lại `planAt` (xem newday.ts), nhưng save cũ vẫn mang mốc hôm
 * qua vào ván mới — nên đây là lưới thứ hai: mốc ở "tương lai" thì coi như đã
 * hết hạn, và con vật được nghĩ ngay ở khung hình đầu tiên.
 */
export function dangNghi(minutes: number, planAt: number): boolean {
  const qua = minutes - planAt;
  return qua >= 0 && qua < REPLAN_COOLDOWN;
}

/** Giờ này đã là ĐÊM chưa — cùng mốc với `animalMood` bên animals.ts. */
function dem(s: GameState, content: Content): boolean {
  return s.minutes >= 20 * 60 || s.minutes < (content.balance.dayStartMinutes ?? 360);
}

/**
 * VÒNG TUẦN: tâm mỗi khu đáng canh trên bản đồ đang chơi.
 *
 * Con chó dùng nó để đi tuần bắt sâu bọ; từ Đợt 22 NGƯỜI LÀM rảnh việc cũng đi
 * đúng vòng ấy thay vì đứng đực. Một danh sách chặng, hai kẻ đi — nếu tách hai
 * bản thì "chỗ đáng để mắt tới" sẽ có hai định nghĩa trôi khỏi nhau.
 *
 * Lô ruộng và khu chăn nuôi — đúng những chỗ sâu bọ tìm tới. Thứ tự lấy theo
 * thứ tự khai trong content nên nó cố định, và cả vòng tính lại mỗi lần chứ
 * không nhớ: content đổi qua OTA thì chặng mới có hiệu lực ngay.
 */
export function vongTuan(s: GameState, content: Content): { x: number; y: number }[] {
  const ra: { x: number; y: number }[] = [];
  for (const z of content.tiles.zones ?? [])
    if (z.map === s.mapId && z.kind === "farm")
      ra.push({ x: Math.floor(z.x + z.w / 2), y: Math.floor(z.y + z.h / 2) });
  for (const p of content.tiles.pens ?? [])
    if (p.map === s.mapId && !p.swim)
      ra.push({ x: Math.floor(p.x + p.w / 2), y: Math.floor(p.y + p.h / 2) });
  return ra;
}

export function animalDef(content: Content, id: string): AnimalDef | null {
  return content.animals[id] ?? null;
}

/** Tốc độ và hộp va chạm của MỘT actor bất kỳ — vật nuôi tra `content.animals`,
 *  người làm thuê tra `content.workers`. Một chỗ hỏi, hai bảng trả lời. */
export function actorShape(
  content: Content,
  e: Entity,
): { speed: number; box: { w: number; h: number }; swims: boolean } | null {
  if (e.kind === "worker")
    return { speed: content.workers.speed, box: content.workers.box, swims: false };
  if (e.kind === "vehicle") {
    const v = content.vehicles[e.def];
    /* THUYỀN "bơi" theo đúng nghĩa của `blockedForActor`: với nó mặt nước là
       chỗ đi được và mặt đất là chỗ chặn. Thiếu chỗ này thì con thuyền sinh ra
       giữa biển và không nhúc nhích được một pixel nào — mọi ô quanh nó bị coi
       là ô đặc. */
    return v ? { speed: v.speed, box: v.box, swims: v.sea === true } : null;
  }
  const def = animalDef(content, e.def);
  return def ? { speed: def.speed, box: def.box, swims: def.housing === "water" } : null;
}

/** Thực thể trên bản đồ ĐANG chơi. Mọi vòng lặp trong TICK phải đi qua đây. */
export function activeEntities(s: GameState): Entity[] {
  return s.entities.filter((e) => e.map === s.mapId);
}

export function entityById(s: GameState, id: number): Entity | null {
  return s.entities.find((e) => e.id === id) ?? null;
}

/** Thực thể đứng trên ô (x,y) của bản đồ đang chơi. */
export function entityAt(s: GameState, x: number, y: number): Entity | null {
  for (const e of s.entities) {
    if (e.map !== s.mapId) continue;
    if (Math.floor(e.x / TILE) === x && Math.floor(e.y / TILE) === y) return e;
  }
  return null;
}

/* ------------------------------------------------------------------- sinh */

export interface SpawnOptions {
  def: string;
  map: string;
  x: number;
  y: number;
  kind?: Entity["kind"];
  /**
   * HẠT ban đầu, do chỗ gọi đưa vào — dùng khi việc sinh xảy ra TRONG `TICK`.
   *
   * Mặc định `spawnEntity` rút hạt từ `state.seed`, và điều đó đúng vì sinh
   * thực thể xưa nay luôn là một ACTION (mua con vật, gọi xe thu mua). Xe chạy
   * trên quốc lộ thì không: nó sinh ra từ đồng hồ, tức từ `TICK`. Mà `TICK`
   * tuyệt đối không được đụng dòng ngẫu nhiên dùng chung — số lần rút sẽ phụ
   * thuộc fps, và bất biến "cùng seed + cùng chuỗi action = state y hệt" vỡ
   * âm thầm (kịch bản 55 canh đúng chỗ đó, và nó đã bắt được tôi một lần).
   *
   * Đưa hạt vào thì `state.seed` không bị đụng tới.
   */
  seed?: number;
}

/**
 * Thêm một thực thể. Trả id, hoặc null nếu content không có loài đó / đã chạm
 * trần. Hạt ngẫu nhiên lấy từ `state.seed` một lần duy nhất ở đây — sinh ra
 * luôn là một ACTION, nên đây là ngẫu nhiên theo sự kiện, không phải theo khung
 * hình.
 */
export function spawnEntity(d: Draft, content: Content, o: SpawnOptions): number | null {
  // Tra ĐÚNG bảng theo loại: vật nuôi ở `content.animals`, xe ở
  // `content.vehicles`. Chỉ hỏi bảng vật nuôi thì xe không bao giờ sinh được —
  // và lỗi đó lặng lẽ rơi về "thả thẳng con vật xuống", tức là mất luôn tính
  // năng giao hàng mà không có thông báo nào.
  const def =
    o.kind === "vehicle"
      ? (content.vehicles[o.def] ?? null)
      : animalDef(content, o.def);
  if (!def) return null;
  if (d.s.entities.length >= MAX_ENTITIES) return null;

  const s = touch(d);
  const id = s.entSeq + 1;
  s.entSeq = id;
  /* Hạt ban đầu rút từ `state.seed` — sinh thực thể là một ACTION nên rút ở
     đây là ngẫu nhiên theo SỰ KIỆN, đúng khuôn cũ. Từ giây này trở đi con vật
     chỉ dùng hạt riêng của nó và không đụng `state.seed` nữa.
     Chỗ gọi TỪ TRONG TICK phải tự đưa hạt vào (`o.seed`) — xem SpawnOptions.

     (Cố ý KHÔNG import `core/rng.ts`: chiều phụ thuộc là core → game, một
      chiều. `game/` chỉ được phép biết đúng `core/version.ts`.) */
  const tuNgoai = Number.isFinite(o.seed);
  const r = tuNgoai ? { seed: (o.seed as number) >>> 0 } : nextRandom(s.seed);
  if (!tuNgoai) s.seed = r.seed;

  const e: Entity = {
    id,
    kind: o.kind ?? ("job" in def && def.job === "pest" ? "pest" : "animal"),
    def: o.def,
    map: o.map,
    x: o.x,
    y: o.y,
    dir: "down",
    anim: 0,
    seed: (r.seed ^ (id * 2654435761)) >>> 0,
    ai: { phase: "idle", until: 0, tx: -1, ty: -1, path: [], planAt: -999 },
    animal:
      "fedMinutes" in def
        ? { age: 0, fed: def.fedMinutes, hungryDays: 0, prod: def.products.map(() => 0) }
        : { age: 0, fed: 0, hungryDays: 0, prod: [] },
  };
  setEntities(d, [...s.entities, e]);
  return id;
}

export function removeEntity(d: Draft, id: number): boolean {
  const i = d.s.entities.findIndex((e) => e.id === id);
  if (i < 0) return false;
  const list = d.s.entities.slice();
  list.splice(i, 1);
  setEntities(d, list);
  return true;
}

/* CON VẬT KHÔNG DỪNG LẠI VÌ NGƯỜI CHƠI. Chấm hết.

   Ở đây từng có hai vành: `calmedByPlayer` (đứng phắt trong 2 ô) và
   `warySpeedMul` (chậm còn 25 % từ 4,5 ô). Cả hai sinh ra để chữa một lo ngại:
   "nhắm vào con bò, bấm, mà trong lúc tay chưa chạm tới thì nó đã nhích ra
   ngoài tầm".

   Đo lại bằng chính con số trong content thì lo ngại đó không đứng vững:

     trễ của một thao tác = actionSeconds 0,42 × (1 − actionImpact 0,5) = 0,21 s
     con nhanh nhất (gà)  = 30 px/s  → nhích 0,39 ô
     tầm với animalNear   = 1,4 ô

   Con nhanh nhất cũng chỉ đi hết một phần tư tầm với trong lúc thao tác đang
   chạy. Cái giá thì Cường trả suốt: cả nông trại hoá thành vùng bất động mỗi
   khi đi ngang, và anh phải nói ba lần mới hết. Bỏ hẳn.

   Nếu sau này lại thấy bấm hụt con vật, chỗ sửa là TẦM VỚI của thao tác, không
   phải đóng băng thế giới. */

/** Con vật này có đang đứng trên ô ĐÃ CÀY không. */
export function onFarmTile(s: GameState, e: Entity): boolean {
  const t = tileAt(s, Math.floor(e.x / TILE), Math.floor(e.y / TILE));
  return !!t?.tilled;
}

/**
 * Ô KHÔNG PHẢI RUỘNG gần nhất để con vật tránh ra, hoặc null.
 *
 * Quét vòng từ trong ra: con vật bị cày dưới chân chỉ cần bước ra khỏi luống,
 * không cần đi đâu xa — mà bước ngắn thì cũng đỡ giẫm thêm mấy ô khác.
 */
function offFarmGoal(
  s: GameState,
  content: Content,
  e: Entity,
  box: { w: number; h: number },
): { x: number; y: number } | null {
  const cx = Math.floor(e.x / TILE);
  const cy = Math.floor(e.y / TILE);
  for (let r = 1; r <= 6; r++)
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x < 1 || y < 1 || x >= s.w - 1 || y >= s.h - 1) continue;
        const t = tileAt(s, x, y);
        if (!t || t.tilled || t.crop) continue;
        if (blockedForActor(s, content, x * TILE + TILE / 2, y * TILE + TILE / 2, box.w, box.h, false))
          continue;
        return { x, y };
      }
  return null;
}

/* ------------------------------------------------------------- di chuyển */

/* `dirOf` cũ là bản sao thứ hai của cùng một luật — nay dùng chung
   `dirFromVector` của world.ts. */

/**
 * Nhích mọi thực thể của bản đồ ĐANG chơi theo đường đi của chúng.
 *
 * Chạy mỗi khung hình và KHÔNG rút một hạt ngẫu nhiên nào — đó là điều kiện để
 * luật 1 đứng vững. Va chạm tách theo trục X rồi Y y hệt `movePlayer`, nên con
 * vật trượt dọc tường thay vì dính góc, và tuyệt đối không lọt vào ô đặc (bất
 * biến `checkInvariants` sẽ ném lỗi nếu lọt).
 */
export function moveActors(d: Draft, content: Content, dt: number): void {
  if (!(dt > 0)) return;
  const s = d.s;
  /* Mưa bão làm CẢ ĐÀN chậm lại — một hệ số cho mọi thứ trên bản đồ, tính
     một lần mỗi khung. Nhân vào bước chứ không vào `speed` của content, cùng lý
     do với vành dè chừng bên dưới. Hệ số ≤ 1 nên A* (ước lượng bằng tốc độ
     content) vẫn không bao giờ đoán thấp. */
  const troi = weatherMood(s, content).speedMul;
  for (let i = 0; i < s.entities.length; i++) {
    const cur = s.entities[i]!;
    if (cur.map !== s.mapId) continue;

    if (!cur.ai.path.length) continue;

    const def = actorShape(content, cur);
    if (!def) continue;

    const target = cur.ai.path[0]!;
    const tx = (target % s.w) * TILE + TILE / 2;
    const ty = ((target / s.w) | 0) * TILE + TILE / 2;
    const dx = tx - cur.x;
    const dy = ty - cur.y;
    const len = Math.hypot(dx, dy);

    const e = dEntity(d, i);
    if (!e) continue;

    if (len < 1.5) {
      // tới điểm mốc: bỏ nó đi, khung hình sau đi tiếp điểm kế
      e.ai.path = e.ai.path.slice(1);
      e.x = tx;
      e.y = ty;
      continue;
    }

    /* Chậm dần lại khi người chơi tới gần. Nhân vào BƯỚC chứ không vào `speed`
       của content: content nói con vật đi nhanh bao nhiêu, còn đây là chuyện
       nó đang dè chừng — hai thứ khác nhau, và trộn vào nhau thì mỗi lần chỉnh
       cân bằng lại phải nhớ trừ hao cho cái vành này. */
    const step = Math.min(len, def.speed * dt * troi);
    const nx = cur.x + (dx / len) * step;
    const ny = cur.y + (dy / len) * step;
    let mx = cur.x;
    let my = cur.y;
    if (!blockedForActor(s, content, nx, cur.y, def.box.w, def.box.h, def.swims)) mx = nx;
    if (!blockedForActor(s, content, mx, ny, def.box.w, def.box.h, def.swims)) my = ny;

    if (mx === cur.x && my === cur.y) {
      // Kẹt: bỏ đường, bước quyết định kế tiếp sẽ tính lại. Không cố dí vào
      // tường mãi như bản đầu của bấm-để-đi từng làm.
      e.ai.path = [];
      continue;
    }
    e.x = mx;
    e.y = my;
    e.dir = dirFromVector(mx - cur.x, my - cur.y, cur.dir);
    e.anim = cur.anim + dt;
  }
}

/* --------------------------------------------------------- ngẫu nhiên cục bộ */

/** Rút một số [0,1) từ hạt RIÊNG của con vật và ghi hạt mới vào chính nó.
 *  Không bao giờ đụng `state.seed`. */
function roll(e: Entity): number {
  const r = nextRandom(e.seed);
  e.seed = r.seed;
  return r.v;
}

/* ------------------------------------------------------------ bước quyết định */

/**
 * Ô đích ngẫu nhiên quanh vị trí hiện tại, trong bán kính `r` ô.
 *
 * Thử vài lần để né RUỘNG. Không thử lại thì con bò cứ nhắm thẳng vào luống rau
 * mà đi, và tuy đường đi đã tránh ruộng thì cái ĐÍCH vẫn nằm giữa luống.
 */
function wanderGoal(e: Entity, s: GameState, r: number, avoidFarm: boolean): { x: number; y: number } {
  const cx = Math.floor(e.x / TILE);
  const cy = Math.floor(e.y / TILE);
  let best = { x: cx, y: cy };
  for (let k = 0; k < 4; k++) {
    const dx = Math.floor(roll(e) * (r * 2 + 1)) - r;
    const dy = Math.floor(roll(e) * (r * 2 + 1)) - r;
    const g = {
      x: Math.max(1, Math.min(s.w - 2, cx + dx)),
      y: Math.max(1, Math.min(s.h - 2, cy + dy)),
    };
    best = g;
    if (!avoidFarm) break;
    if (!s.tiles[g.y * s.w + g.x]?.tilled) break;
  }
  return best;
}

/**
 * Một bước quyết định cho MỌI thực thể của bản đồ đang chơi.
 *
 * Ngân sách tìm đường: mỗi bước chỉ `MAX_REPLANS_PER_STEP` con được gọi A*, và
 * lượt xoay vòng theo `planCursor`. Nhờ vậy chi phí A* là HẰNG SỐ theo số con —
 * 20 con hay 60 con thì vẫn ngần ấy lần tìm đường mỗi giây.
 */
export function actorStep(d: Draft, content: Content, tuiChung: { left: number } | null = null): void {
  if (!d.s.entities.length) return;

  // Chó bắt sâu bọ TRƯỚC khi ai kịp nghĩ: làm ở đây thì nó bắt được cả ban ngày
  // lúc đang đuổi, chứ không phải chỉ một lần lúc người chơi đi ngủ.
  // Đọc `d.s` SAU lời gọi này: bắt được con nào là draft đã thay mảng thực thể,
  // giữ tham chiếu cũ thì cả bước quyết định chạy trên danh sách đã lỗi thời.
  patrolCatch(d, content);
  const s = d.s;
  if (!s.entities.length) return;

  let budget = tuiChung ? tuiChung.left : MAX_REPLANS_PER_STEP;
  const n = s.entities.length;
  const start = ((s.planCursor % n) + n) % n;

  for (let k = 0; k < n; k++) {
    /* Đọc LẠI mảng mỗi vòng: một lượt của actor có thể THÊM (xe thả con vật
       xuống) hoặc BỚT (xe ra khỏi bản đồ) phần tử, nên độ dài chốt từ đầu vòng
       lặp sẽ sai ngay sau đó. Không đọc lại thì `s.entities[i]` ra undefined và
       cả TICK ném lỗi — âm thầm cho tới đúng lúc chiếc xe đầu tiên rời đi. */
    const list = s.entities;
    if (!list.length) break;
    const i = (start + k) % list.length;
    const cur = list[i];
    if (!cur) continue;
    if (cur.map !== s.mapId) continue;
    const def = actorShape(content, cur);
    if (!def) continue;

    // Còn đường để đi thì cứ đi, không phải nghĩ gì.
    if (cur.ai.path.length) continue;

    // Người làm thuê có bộ não riêng (workers.ts). Trả về true nghĩa là họ đã
    // tự lo xong lượt này — vật nuôi mới đi tiếp nhánh lang thang bên dưới.
    const budgetFn = () => budget > 0 && (budget--, true);
    if (cur.kind === "worker") {
      if (workerStep(d, content, i, budgetFn)) continue;
    }
    if (cur.kind === "vehicle") {
      if (vehicleStep(d, content, i, budgetFn)) continue;
    }

    const e = dEntity(d, i);
    if (!e) continue;

    /* ĐÓI THÌ ĂN, TRƯỚC MỌI CỔNG.

       Ăn không tốn một nút A* nào: nó chỉ hỏi "sát bên có máng còn đồ không",
       "dưới chân có cỏ không". Vậy mà trước đây nó nằm sau BỐN cái cổng — đồng
       hồ nghỉ, xúc xắc nghỉ 45 %, ngân sách A*, đồng hồ nguội — tất cả dựng lên
       để chặn CHI PHÍ TÌM ĐƯỜNG, và chặn nhầm cả bữa ăn. Con vật đứng sát máng
       đầy vẫn phải ngủ cho hết giấc rồi bốc thăm mới được ăn.

       Đo được: cùng một con bò đứng cạnh máng, đói. Hỏi bữa trước thì nó ăn ở
       bước quyết định kế tiếp; hỏi sau bốn cổng thì mất sáu giây thật, và trong
       một đàn đông thì lâu hơn nữa.

       Bốn cổng kia vẫn nguyên vẹn cho phần TÌM ĐƯỜNG bên dưới — thứ chúng sinh
       ra để canh. */
    const dinhDuong = cur.kind === "animal" ? animalDef(content, cur.def) : null;
    const doi = !!dinhDuong && dinhDuong.job !== "pest" && isHungry(cur);
    if (doi) {
      // Đứng sát MÁNG mà máng còn thức ăn thì ăn ngay — bữa chắc chắn, và no
      // HẲN. Xét trước cỏ: con vật đứng cạnh máng đầy mà vẫn cúi gặm cỏ là thứ
      // nhìn vào thấy sai ngay, và làm cái máng thành vô nghĩa.
      if (eatFromTrough(d, content, i)) continue;
      if (grazeHere(d, content, activeView(d), i)) continue;
    }

    // Đồng hồ giai đoạn: đứng nhai cỏ / ngủ thì đếm ngược ở đây.
    if (e.ai.until > 0) {
      e.ai.until = Math.max(0, e.ai.until - ACTOR_STEP_MINUTES);
      if (e.ai.until > 0) continue;
    }

    /* ĐỨNG TRÊN LUỐNG thì việc đầu tiên là đi ra — trước cả chuyện nghỉ ngơi
       hay gặm cỏ. Người chơi vừa cày ngay dưới chân nó, và một con bò đứng ì
       giữa luống mới cày là thứ nhìn thấy ngay và thấy là khó chịu. */
    if (cur.kind === "animal" && onFarmTile(s, cur)) {
      if (budget > 0 && !dangNghi(s.minutes, e.ai.planAt)) {
        const shape = actorShape(content, cur);
        const ra = shape ? offFarmGoal(s, content, cur, shape.box) : null;
        if (ra) {
          e.ai.planAt = s.minutes;
          budget--;
          const px = Math.floor(e.x / TILE);
          const py = Math.floor(e.y / TILE);
          const p = findPath(s, content, px, py, new Set([idx(s.w, ra.x, ra.y)]), {
            maxNodes: MAX_NODES_ACTOR,
            box: shape!.box,
            swims: shape!.swims,
            // Cố ý KHÔNG `avoidFarm`: nó đang Ở TRONG ruộng, cấm đi qua ruộng
            // thì không có đường nào ra cả.
            leash: { x: px, y: py, r: 8 },
          });
          if (p && p.length) {
            e.ai.path = p.slice(0, MAX_PATH);
            e.ai.phase = "wander";
            e.ai.until = 0;
            continue;
          }
        }
      }
    }

    // Hết việc: đứng nghỉ một lát rồi mới lang thang tiếp — con vật đứng yên
    // gặm cỏ nhìn tự nhiên hơn hẳn con vật chạy không ngừng.
    if (roll(e) < 0.45) {
      e.ai.phase = "idle";
      e.ai.until = 1 + roll(e) * 3;
      continue;
    }

    if (budget <= 0) continue;
    if (dangNghi(s.minutes, e.ai.planAt)) continue;

    // Vật nuôi tránh ruộng; sâu bọ thì KHÔNG — phá cây là việc của chúng.
    const neRuong = cur.kind === "animal";

    /* Con chó ĐI TUẦN chứ không lang thang: nó nhắm thẳng vào con sâu bọ gần
       nhất. Không có cái này thì nó đi ngẫu nhiên trên bản đồ 48×37 và gần như
       không bao giờ đứng đủ gần con chuột nào để đuổi — nuôi chó thành ra vô
       nghĩa, đúng thứ người chơi sẽ nhận ra ngay sau vài đêm. */
    let g: { x: number; y: number } | null = null;

    /* VỀ KHU của mình. Hai vai:
         · đói + máng còn ăn → nhắm thẳng ô kề máng (bữa chắc chắn hơn đi tìm cỏ)
         · đang ở NGOÀI khu   → nhắm về ruột khu
       Rào có cổng nên đây là "tự về chuồng", không phải "bị nhốt": máng cạn thì
       nhánh tìm cỏ bên dưới vẫn dắt nó ra ngoài gặm như trước. */
    let khu = cur.kind === "animal" ? penOf(content, cur) : null;
    if (khu && khu.map !== s.mapId) khu = null;
    if (cur.kind === "animal") g = penGoal(s, content, cur, doi);

    /* TRỜI XẤU thì TRÚ. Đọc từ content (`weather.shelter` / `halt`), chỉ ngoài
       trời, và chỉ cho VẬT NUÔI — sâu bọ phá hoại mặc kệ mưa gió.

         · mưa (`shelter`): có chuồng thì `penGoal` đã dắt về; ở trong rồi thì
           đứng yên (co ro), không loanh quanh. Thả rông thì ra nép gốc cây gần
           nhất. Con ĐÓI vẫn được ra bãi cỏ — mưa dầm ba ngày mà nhịn là mất
           trứng, mà không ai muốn con gà chết vì một cơn mưa.
         · bão (`halt`): kể cả đói cũng không ra — một ngày, chịu được.
       Con chó cũng thế: `penGoal` đưa nó về nhà chó, rồi nó nằm đó — không
       tuần, không đuổi chuột giữa bão. */
    const troi = weatherMood(s, content);
    const tru = cur.kind === "animal" && !!dinhDuong && dinhDuong.job !== "pest" && troi.shelter;

    /* Đói thì nhắm thẳng vào bãi cỏ gần nhất thay vì lang thang. Bán kính hẹp
       (8 ô) cho ban ngày: con vật đi trong vài phút game thì không thể băng cả
       nông trại, và quét rộng mỗi bước cho từng con là thứ giết fps trước tiên.
       Đêm thì `grazeNight` quét rộng hơn hẳn — cả một đêm thì nó đi được xa. */
    if (!g && doi && dinhDuong && !(tru && troi.halt)) {
      g = nearestGraze(s, content, dinhDuong, cur, 8);
    }

    if (!g && tru) {
      if (!khu) {
        const nep = shelterSpot(s, content, cur, def.box);
        if (nep && (nep.x !== Math.floor(cur.x / TILE) || nep.y !== Math.floor(cur.y / TILE))) g = nep;
      }
      if (!g) {
        // Đã ở chỗ trú (trong chuồng, dưới gốc cây, hoặc không có chỗ nào): đứng yên.
        e.ai.phase = "idle";
        e.ai.until = 2 + roll(e) * 4;
        e.ai.planAt = s.minutes;
        continue;
      }
    }

    if (!g && !tru && animalDef(content, cur.def)?.job === "patrol") {
      let bestD = Infinity;
      for (const p of s.entities) {
        if (p.map !== s.mapId || animalDef(content, p.def)?.job !== "pest") continue;
        const dd = Math.hypot(p.x - e.x, p.y - e.y);
        if (dd < bestD) {
          bestD = dd;
          g = { x: Math.floor(p.x / TILE), y: Math.floor(p.y / TILE) };
        }
      }
      /* HẾT SÂU BỌ THÌ VẪN LÀ ĐI TUẦN, không phải lang thang.

         Trước đây `job: "patrol"` chỉ có nghĩa "nhắm thẳng con sâu gần nhất";
         không có con nào thì con chó rơi xuống `wanderGoal` bán kính 4 và đi
         loanh quanh y hệt con gà. Cường nói đúng: "chó tự đi tuần tra xung
         quanh". Một con chó canh nhà thì phải ĐI QUA những chỗ đáng canh.

         Vòng tuần = tâm của từng KHU đáng canh (lô ruộng, chuồng, nhà chó),
         theo thứ tự cố định trong content, mỗi lần một chặng. Chọn chặng bằng
         `actStep` chứ không bằng ngẫu nhiên: cùng seed thì cùng lộ trình, và
         đó là điều kiện để kịch bản kiểm được nó.

         BAN ĐÊM thì thôi tuần, về nằm ở nhà mình — `penGoal` lo phần đường về;
         ở đây chỉ cần KHÔNG nhận chặng tuần mới. */
      if (!g && !dem(s, content)) {
        const chang = vongTuan(s, content);
        if (chang.length) {
          const k2 = Math.abs((s.actStep / 40) | 0) % chang.length;
          const t2 = chang[k2]!;
          // Đang đứng ngay tại chặng này rồi thì đi tiếp chặng sau.
          const tai = Math.abs(t2.x - cur.x / TILE) < 2 && Math.abs(t2.y - cur.y / TILE) < 2;
          g = tai ? (chang[(k2 + 1) % chang.length] ?? t2) : t2;
        }
      }
    }
    /* Loanh quanh TRONG KHU của mình. Tới đây nghĩa là con vật đã ở trong khu
       (`penGoal` trả null khi đã về tới) và không phải đi kiếm ăn — nên chỗ nó
       đi loanh quanh là ruột chuồng, không phải một hình vuông bán kính 4
       quanh chỗ đứng. Ruột chuồng cao đúng 3 ô, nên hình vuông ấy gần như lần
       nào cũng chỉ ra ngoài, và con vật lách qua cổng đi mất dù còn no. */
    if (!g && khu) g = penWander(s, cur, khu);
    if (!g) g = wanderGoal(e, s, 4, neRuong);
    e.ai.planAt = s.minutes;
    budget--;

    const cx = Math.floor(e.x / TILE);
    const cy = Math.floor(e.y / TILE);
    const path = findPath(s, content, cx, cy, new Set([idx(s.w, g.x, g.y)]), {
      maxNodes: MAX_NODES_ACTOR,
      box: def.box,
      swims: def.swims,
      // Chó đang đuổi thì được phép băng qua ruộng — đứng ngoài bờ nhìn con
      // chuột gặm cây thì tuần tra để làm gì.
      avoidFarm: neRuong && animalDef(content, cur.def)?.job !== "patrol",
      // Loài bơi bị nhốt trong cái ao của nó: bán kính nhỏ, không đi lang thang
      // sang ao khác qua đường bộ (mà nó cũng không đi bộ được).
      /* Dây buộc là HỘP quanh tâm, và ô XUẤT PHÁT không được kiểm — nên hộp
         phải chứa CẢ con vật lẫn đích, nếu không con bò đi lạc quá 20 ô sẽ
         không bao giờ tìm được đường về chuồng (mọi ô kề đều rơi ra ngoài hộp).

         Luật này áp cho MỌI đích, không riêng đường về khu. Trước đây chỉ nhánh
         "về khu" được nới, nên con chó đi tuần dính đúng cái bẫy ấy: hộp bán
         kính 20 quanh CHÍNH NÓ, mà bản đồ rộng 48 ô — con chuột ở nửa kia thì
         không có đường nào, và con chó đứng im. Nuôi chó thành ra vô nghĩa, còn
         `patrolCatch` thì chưa từng có một dòng test nào để lộ ra.

         Chỉ nhánh LANG THANG không đích mới giữ hộp nhỏ: đó mới là chỗ dây buộc
         thật sự có việc — giữ con vật khỏi đi lạc cả bản đồ. */
      leash: {
        x: Math.round((cx + g.x) / 2),
        y: Math.round((cy + g.y) / 2),
        r: Math.max(
          def.swims ? 6 : LEASH_TILES,
          Math.max(Math.abs(cx - g.x), Math.abs(cy - g.y)) / 2 + 6,
        ),
      },
    });
    if (path && path.length) {
      e.ai.path = path.slice(0, MAX_PATH);
      e.ai.phase = "wander";
    } else {
      /* Không có đường tới đích. Với đích KIẾM ĂN thì "đứng nghỉ rồi thử lại"
         là một cái bẫy: bước sau `penGoal` trả về ĐÚNG cái đích chết ấy, và nó
         lặp mãi — con vật đứng nguyên một chỗ tới lúc chết đói cạnh máng đầy.
         Thử một đích lang thang trong khu ngay trong bước này; đi được vài ô là
         khoảng cách đổi, và đích kế tiếp có thể tới được. */
      let cuu: { x: number; y: number } | null = null;
      if (doi && khu) cuu = penWander(s, cur, khu);
      const p2 = cuu
        ? findPath(s, content, cx, cy, new Set([idx(s.w, cuu.x, cuu.y)]), {
            maxNodes: MAX_NODES_ACTOR,
            box: def.box,
            swims: def.swims,
            avoidFarm: neRuong,
            leash: { x: cx, y: cy, r: LEASH_TILES },
          })
        : null;
      if (p2 && p2.length) {
        e.ai.path = p2.slice(0, MAX_PATH);
        e.ai.phase = "wander";
      } else {
        e.ai.phase = "idle";
        e.ai.until = 1 + roll(e) * 2;
      }
    }
  }

  touch(d).planCursor = (start + MAX_REPLANS_PER_STEP) % Math.max(1, n);
  if (tuiChung) tuiChung.left = budget;
}

/**
 * Chạy đủ số bước quyết định còn thiếu. Gọi từ TICK sau khi `minutes` đã cộng.
 *
 * Số bước suy ra từ `minutes` chứ không tích luỹ `dt`, nên nó chỉ phụ thuộc
 * TỔNG thời gian game đã trôi — không phụ thuộc fps, không trôi qua save.
 */
export function runActorSteps(d: Draft, content: Content): void {
  const want = Math.floor(d.s.minutes / ACTOR_STEP_MINUTES);
  let n = 0;
  /* Bước ĐẦU của một TICK có trọn `MAX_REPLANS_PER_STEP` như thường. Các bước
     CHẠY BÙ sau đó (tab quay lại, cổng dịch chuyển) dùng chung MỘT túi nữa —
     không phải mỗi bước một túi mới. Trước đây 8 bước × 2 lượt A* × 2.000 nút
     = 32.000 lần mở nút gói trong một khung hình. Lối chơi bình thường (một
     bước mỗi TICK) không đổi một ly. */
  const buChung = { left: MAX_REPLANS_PER_STEP };
  while (d.s.actStep < want && n < MAX_STEPS_PER_TICK) {
    actorStep(d, content, n === 0 ? null : buChung);
    touch(d).actStep = d.s.actStep + 1;
    n++;
  }
  // Nhảy vọt (nạp save, migrate, DEBUG sang ngày): đồng bộ thẳng, đừng chạy bù
  // hàng nghìn bước.
  if (d.s.actStep < want) touch(d).actStep = want;
  if (d.s.actStep > want) touch(d).actStep = want;
}

/* ------------------------------------------------------------- cộng bù */

/**
 * Cộng bù ĐỒNG HỒ cho thực thể trên một bản đồ vắng mặt.
 *
 * Chỉ đồng hồ, KHÔNG BAO GIỜ vị trí. Con bò trong chuồng vẫn phải đói và vẫn ra
 * sữa khi người chơi đang ở trong nhà, nhưng mô phỏng đường đi của nó lúc không
 * ai nhìn thì vừa đắt vừa vô nghĩa. Đúng cùng cách `growCropsIn` đang làm cho
 * cây trồng: chỉ cộng cái cộng được bằng số học.
 */
export function catchUpEntities(
  d: Draft,
  content: Content,
  mapId: string,
  minutes: number,
): void {
  if (!(minutes > 0)) return;
  for (let i = 0; i < d.s.entities.length; i++) {
    const cur = d.s.entities[i]!;
    if (cur.map !== mapId) continue;
    const def = animalDef(content, cur.def);
    if (!def) continue;
    if (def.job === "pest") continue; // sâu bọ có vòng đời riêng, không đói không ra sản phẩm
    const e = dEntity(d, i);
    if (!e) continue;
    e.animal.fed = Math.max(0, e.animal.fed - minutes);
    for (let j = 0; j < e.animal.prod.length; j++) e.animal.prod[j] = (e.animal.prod[j] ?? 0) + minutes;
  }
}

/** Bỏ mọi thực thể có `def` hoặc `map` không còn trong content. Dùng khi migrate. */
export function pruneEntities(list: Entity[], content: Content): { list: Entity[]; dropped: string[] } {
  const dropped: string[] = [];
  const out = list.filter((e) => {
    // Người làm thuê không nằm trong bảng loài — họ có bảng cấu hình riêng, nên
    // đừng đem `content.animals` ra hỏi rồi xoá sạch họ lúc cập nhật content.
    if (e.kind === "vehicle") {
      // Thiếu khối `veh` là xe hỏng — bỏ đi lúc migrate, nếu không save cũ sẽ
      // ném lỗi bất biến sau mỗi tick cho tới hết đời.
      if (!content.vehicles[e.def] || !e.veh) {
        dropped.push(e.def);
        return false;
      }
      return !!content.maps[e.map];
    }
    if (e.kind !== "worker" && !content.animals[e.def]) {
      dropped.push(e.def);
      return false;
    }
    if (!content.maps[e.map]) {
      dropped.push(e.def);
      return false;
    }
    return true;
  });
  return { list: out, dropped };
}

/** Dọn danh sách sau khi số lượng vượt trần (content đổi, save lạ). */
export function capEntities(list: Entity[]): Entity[] {
  return list.length <= MAX_ENTITIES ? list : list.slice(0, MAX_ENTITIES);
}

export { dEntities };
